import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Button,
  Alert,
  Box,
  ColumnLayout
} from '@cloudscape-design/components';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFederateLoading, setIsFederateLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { signIn, signInWithFederate } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the return URL from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Define handleFederatedLogin function
  const handleFederatedLogin = () => {
    setIsFederateLoading(true);
    setErrorMessage('');
    
    try {
      // Store the intended destination in sessionStorage for the callback
      sessionStorage.setItem('oauth2_redirect_after_login', from);
      signInWithFederate();
    } catch (error: any) {
      console.error('Federated login error:', error);
      setErrorMessage(error.message || 'Failed to initiate federated login');
      setIsFederateLoading(false);
    }
  };

  // Check for error message from location state (e.g., from OAuth2 callback failures)
  useEffect(() => {
    if (location.state?.error) {
      setErrorMessage(location.state.error);
    }
  }, [location.state]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      await signIn(email, password);
      // Redirect to the page they were trying to access, or dashboard
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      // Display user-friendly error message
      if (error.code === 'UserNotConfirmedException') {
        setErrorMessage('Please confirm your account before logging in.');
      } else if (error.code === 'NotAuthorizedException') {
        setErrorMessage('Incorrect username or password.');
      } else {
        setErrorMessage(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <SpaceBetween size="l">
          <Header
            variant="h1"
            description="Enter your email and password to access the Customer Feedback Admin Portal"
          >
            Sign in to Customer Feedback
          </Header>
          
          {errorMessage && (
            <Alert type="error" dismissible={true} onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="h3" padding={{ bottom: 's' }}>
                Admin Login
              </Box>
              <Form>
                <SpaceBetween size="m">
                  <FormField label="Email">
                    <Input
                      type="text"
                      value={email}
                      onChange={event => setEmail(event.detail.value)}
                      placeholder="email@example.com"
                      disabled={isLoading || isFederateLoading}
                    />
                  </FormField>
                  
                  <FormField label="Password">
                    <Input
                      type="password"
                      value={password}
                      onChange={event => setPassword(event.detail.value)}
                      placeholder="Your password"
                      disabled={isLoading || isFederateLoading}
                    />
                  </FormField>
                  
                  <Button
                    variant="primary"
                    formAction="submit"
                    loading={isLoading}
                    disabled={isLoading || isFederateLoading}
                    fullWidth
                  >
                    Sign In with Credentials
                  </Button>
                </SpaceBetween>
              </Form>
            </div>

            <div>
              <Box variant="h3" padding={{ bottom: 's' }}>
                Federated Login
              </Box>
              <SpaceBetween size="m">
                <Box variant="p" color="text-body-secondary">
                  Use your corporate credentials to sign in through Midway authentication.
                </Box>
                <Button
                  variant="normal"
                  onClick={handleFederatedLogin}
                  loading={isFederateLoading}
                  disabled={isLoading || isFederateLoading}
                  fullWidth
                >
                  Proceed with Midway
                </Button>
              </SpaceBetween>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </form>
    </Container>
  );
};

export default Login;
